import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Home,
  FileText,
  DollarSign,
  Users,
  Star,
  Settings,
  BarChart3,
  Flag,
  MessageSquareWarning,
  UserCog,
  Building2,
  Trophy,
  BookOpen,
  Rss,
  TrendingUp,
  Newspaper,
} from 'lucide-react';

type PortalType = 'creator' | 'admin' | 'org';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const creatorNavItems: NavItem[] = [
  { path: '/creator', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
  { path: '/creator/questions', label: 'My Questions', icon: <FileText className="w-5 h-5" /> },
  { path: '/creator/submit', label: 'Submit Question', icon: <BookOpen className="w-5 h-5" /> },
  { path: '/creator/reviews', label: 'Peer Reviews', icon: <Star className="w-5 h-5" /> },
  { path: '/creator/earnings', label: 'Earnings', icon: <DollarSign className="w-5 h-5" /> },
  { path: '/creator/mentorship', label: 'Mentorship', icon: <Users className="w-5 h-5" /> },
  { path: '/creator/settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
];

const adminNavItems: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
  { path: '/admin/questions', label: 'Content CMS', icon: <FileText className="w-5 h-5" /> },
  { path: '/admin/blog', label: 'Blog', icon: <Rss className="w-5 h-5" /> },
  { path: '/admin/clinical-updates', label: 'Clinical Updates', icon: <Newspaper className="w-5 h-5" /> },
  { path: '/admin/question-reports', label: 'Question Reports', icon: <MessageSquareWarning className="w-5 h-5" /> },
  { path: '/admin/users', label: 'Users', icon: <UserCog className="w-5 h-5" /> },
  { path: '/admin/creators', label: 'Creators', icon: <Users className="w-5 h-5" /> },
  { path: '/admin/organizations', label: 'Organizations', icon: <Building2 className="w-5 h-5" /> },
  { path: '/admin/analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
  { path: '/admin/growth-metrics', label: 'Growth Metrics', icon: <TrendingUp className="w-5 h-5" /> },
  { path: '/admin/payments', label: 'Payments', icon: <DollarSign className="w-5 h-5" /> },
  { path: '/admin/flags', label: 'Feature Flags', icon: <Flag className="w-5 h-5" /> },
  { path: '/admin/settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
];

const orgNavItems: NavItem[] = [
  { path: '/org', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
  { path: '/org/residents', label: 'Residents', icon: <Users className="w-5 h-5" /> },
  { path: '/org/analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
  { path: '/org/challenges', label: 'Challenges', icon: <Trophy className="w-5 h-5" /> },
  { path: '/org/leaderboard', label: 'Leaderboard', icon: <Star className="w-5 h-5" /> },
  { path: '/org/billing', label: 'Billing', icon: <DollarSign className="w-5 h-5" /> },
  { path: '/org/settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
];

interface SidebarProps {
  portal: PortalType;
  title?: string;
  subtitle?: string;
}

export function Sidebar({ portal, title, subtitle }: SidebarProps) {
  const location = useLocation();

  const navItems = {
    creator: creatorNavItems,
    admin: adminNavItems,
    org: orgNavItems,
  }[portal];

  const portalInfo = {
    creator: { title: 'Creator Portal', icon: '✏️' },
    admin: { title: 'Admin Portal', icon: '🛡️' },
    org: { title: 'Organization', icon: '🏥' },
  }[portal];

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-cream-300 z-20">
      {/* Header */}
      <div className="h-16 flex items-center gap-3 px-6 border-b border-cream-200">
        <Link to="/app" className="flex items-center gap-2">
          <span className="text-xl">💚</span>
          <span className="text-lg font-bold text-gradient">PulseMD</span>
        </Link>
      </div>

      {/* Portal Title */}
      <div className="px-6 py-4 border-b border-cream-200">
        <div className="flex items-center gap-2">
          <span className="text-xl">{portalInfo.icon}</span>
          <div>
            <h2 className="font-semibold text-charcoal-600">
              {title || portalInfo.title}
            </h2>
            {subtitle && (
              <p className="text-sm text-charcoal-400">{subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={clsx(
                    'flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200',
                    isActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-charcoal-400 hover:bg-cream-100 hover:text-charcoal-600'
                  )}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-cream-200">
        <Link
          to="/app"
          className="flex items-center gap-3 px-4 py-3 text-charcoal-400 hover:bg-cream-100 hover:text-charcoal-600 rounded-xl transition-colors"
        >
          <Home className="w-5 h-5" />
          <span>Back to Learner App</span>
        </Link>
      </div>
    </aside>
  );
}

export default Sidebar;
